import React, { Component, CSSProperties } from 'react';
import classnames from 'classnames';

import { Button } from 'components/shared';
import { AnchoredPortal } from '../AnchoredPortal';
import Icon from 'assets/icons';

import styles from './PopupButton.module.scss';

interface PopupButtonProps {
  id: string;
  type?: string;
  arrow?: boolean;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipPos?: string;
  tooltipText?: string;
  anchored?: boolean;
  disabled?: boolean;
  className?: string;
  openLabel?: string;
  hideLabel?: boolean;
  hoverToOpen?: boolean;
  expandPopup?: boolean;
  buttonProps?: any;
  alwaysRender?: boolean;
  buttonClassName?: string;
  buttonActiveClass?: string;
  popupClassName?: string;
  closeOnClick?: boolean;
  Component?: any;
  onOpen?: () => void;
  onClose?: () => void;
  onMouseEnter?: (value: boolean) => void;
}

interface PopButtonState {
  open?: boolean;
}

export class PopupButton extends Component<PopupButtonProps, PopButtonState> {
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
  }

  state: PopButtonState = {
    open: false,
  };

  handleToggleClick = () => {
    const { open } = this.state;
    const { onClose, onOpen } = this.props;

    if (!open) {
      window.addEventListener('click', (event) => this.handleOutsideClick(event), true);

      if (onOpen) {
        onOpen();
      }
    } else {
      window.removeEventListener('click', (event) => this.handleOutsideClick(event), true);

      if (onClose) {
        onClose();
      }
    }
    if (this._isMounted) {
      this.setState((prevState) => ({ open: !prevState.open }));
    }
  };

  handleClosePopup = () => {
    if (this.state.open && this.props.onClose) {
      this.props.onClose();
    }

    window.removeEventListener('click', (event) => this.handleOutsideClick(event), true);
    if (this._isMounted) {
      this.setState({ open: false });
    }
  };

  handleHover = () => {
    const { onMouseEnter } = this.props;
    if (typeof onMouseEnter !== 'undefined') {
      onMouseEnter(false);
    }
  };

  handleOutsideClick = (event: any) => {
    const { id } = this.props;
    const elementButton = document.getElementById(`popup-${id}-button`);
    const elementContent = document.getElementById(`popup-${id}-content`);

    if (
      elementContent &&
      !elementContent.contains(event.target) &&
      elementButton &&
      !elementButton.contains(event.target) &&
      this.state.open &&
      !event.target.closest(`#popup-${id}-button`)
    ) {
      // Clicked outside the box
      this.handleClosePopup();
    }
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const {
      id,
      type,
      arrow = false,
      label,
      anchored = false,
      children,
      disabled,
      openLabel,
      className,
      alwaysRender = false,
      buttonProps = {},
      buttonClassName,
      buttonActiveClass,
      closeOnClick = false,
      expandPopup = false,
      hideLabel = false,
      hoverToOpen = false,
      popupClassName,
      tooltipText,
      Component = Button,
    } = this.props;

    const anchorClasses = classnames(className, {
      [styles.anchor]: true,
      [styles.arrowPadding]: arrow,
      [styles.hoverToOpen]: hoverToOpen,
      [styles[`type-${type}__wrapper`]]: type,
    });

    const arrowClasses = classnames({
      [styles.arrow]: true,
      [styles.popupOpen]: this.state.open,
    });

    const popupStyle: CSSProperties = {};
    if (!hoverToOpen) {
      popupStyle.display = this.state.open ? 'block' : 'none';
    }

    const popupClasses = classnames(popupClassName, {
      [styles.popup]: true,
      [styles.expandPopup]: expandPopup,
    });

    const activeClasses = classnames('hideTooltip', buttonActiveClass);

    const content =
      this.state.open || alwaysRender ? (
        <div
          className={popupClasses}
          onClick={closeOnClick ? this.handleClosePopup : undefined}
          style={popupStyle}
          id={`popup-${id}-content`}
        >
          {children}
        </div>
      ) : null;

    return (
      <div className={anchorClasses} id={`popup-${id}-button`} onMouseEnter={this.handleHover}>
        <Component
          {...buttonProps}
          className={classnames(
            !disabled && styles.clickable,
            buttonClassName,
            this.state.open ? activeClasses : '',
            disabled && styles.disabled,
            {
              [styles[`type-${type}`]]: type,
            }
          )}
          tooltipText={tooltipText}
          onClick={!disabled && !hoverToOpen ? this.handleToggleClick : undefined}
        >
          <div className={styles.labelAnchor}>
            {!hideLabel && (
              <div className={styles.label}>{(this.state.open && openLabel) || label}</div>
            )}

            {arrow && (
              <div className={styles.arrowContainer}>
                <Icon.TriangleDown className={arrowClasses} />
              </div>
            )}
          </div>
        </Component>

        {/* Dropdown */}
        <>{anchored ? <AnchoredPortal>{content}</AnchoredPortal> : content}</>
      </div>
    );
  }
}

export default PopupButton;
